import { httpService } from '../..';
import { IEntityWithMessages } from '../../services/HttpService';
import { IApiExecuteRespone } from '../apiInterfaces';
import { ApiUrls } from '../ApiUrls';
import { executeApiRequest } from '../asyncUtils';
import { ISalesShipmentFilters } from '../filters';
import { ILoadState, IWarehouse, IWarehouseGroup } from '../lookups';
import { IUnallocatedProduct } from '../unallocatedProducts';
import * as i from './logisticsInterfaces';

export async function fetchShipmentListItems(filters: ISalesShipmentFilters): Promise<i.IShipmentListItem[]> {
  try {
    const response = await httpService.get(ApiUrls.getShipments(filters));
    return await response.json();
  } catch (e) {
    throw e;
  }
}

export async function fetchShipment(shipmentId: number): Promise<i.IShipmentApiResponse | null> {
  try {
    const response = await httpService.get(ApiUrls.shipment(shipmentId));
    return await response.json();
  } catch (e) {
    throw e;
  }
}

export async function fetchCarrier(carrierId: string): Promise<IApiExecuteRespone<i.ILoadMin[]>> {
  return executeApiRequest(() => httpService.get(ApiUrls.carriers(carrierId)));
}

export async function fetchCarriers(): Promise<IApiExecuteRespone<i.ICarrier[]>> {
  return executeApiRequest(() => httpService.get(ApiUrls.carriers()));
}

export async function fetchCarrierLoads(carrierId: string): Promise<IApiExecuteRespone<i.ILoadMin[]>> {
  return executeApiRequest(() => httpService.get(ApiUrls.carriers(carrierId, { loads: true })));
}

export async function fetchMinLoads(
  dates: {
    startDate: Date;
    endDate?: Date;
  },
  warehouses: string[]
): Promise<IApiExecuteRespone<i.ILoadMin[]>> {
  return executeApiRequest(() => httpService.get(ApiUrls.loads({ dates, warehouses })));
}

export async function fetchLoadRoute(loadNumber: string) {
  return executeApiRequest<i.ILoadRoute>(() => httpService.get(ApiUrls.loads({ loadNumber, type: { route: true } })));
}

export async function createLoad(request: i.ICreateLoadRequest): Promise<IApiExecuteRespone<i.ILoadMin>> {
  return executeApiRequest(() => httpService.post(ApiUrls.loads(), request));
}

export async function advanceLoadState(
  loadNumber: string
): Promise<IApiExecuteRespone<IEntityWithMessages<ILoadState>>> {
  return executeApiRequest(() => httpService.post(ApiUrls.loads({ loadNumber, type: { advance: true } })));
}

export async function addItemsToLoad(
  loadNumber: string,
  unallocatedProducts: IUnallocatedProduct[]
): Promise<IApiExecuteRespone<IEntityWithMessages<i.ILoadApiResponse>>> {
  return executeApiRequest(() =>
    httpService.post(ApiUrls.loads({ loadNumber, type: { shipments: true } }), unallocatedProducts)
  );
}

export async function updateLoadDepartureDate(
  request: i.IChangeLoadDateRequest
): Promise<IApiExecuteRespone<i.ILoadApiResponse>> {
  return executeApiRequest(() =>
    httpService.post(ApiUrls.loads({ loadNumber: request.loadNumber, type: { departureDate: true } }), request)
  );
}

export async function updateLoadFees(loadNumber: string, loadFees: i.ILoadFee[]): Promise<IApiExecuteRespone<i.ILoad>> {
  return executeApiRequest(() => httpService.post(ApiUrls.loads({ loadNumber, type: { fees: true } }), loadFees));
}

export async function updateLoad(
  load: Pick<i.ILoad, 'loadNumber'> & Partial<i.ILoad>
): Promise<IApiExecuteRespone<IEntityWithMessages<i.ILoadApiResponse>>> {
  return executeApiRequest(() => httpService.post(ApiUrls.loads({ loadNumber: load.loadNumber }), load));
}
export async function updateLoadRoute(
  update: i.ILoadRouteUpdateRequest
): Promise<IApiExecuteRespone<IEntityWithMessages<i.ILoadRoute>>> {
  return executeApiRequest(() =>
    httpService.post(ApiUrls.loads({ loadNumber: update.loadNumber, type: { route: true } }), update)
  );
}

export async function updateWarehouse(warehouse: IWarehouse): Promise<IApiExecuteRespone<IWarehouse>> {
  return executeApiRequest(() => httpService.post(ApiUrls.logisticsWarehouses(), warehouse));
}

export async function updateWarehouseGroup(
  group: IWarehouseGroup
): Promise<IApiExecuteRespone<IEntityWithMessages<IWarehouseGroup>>> {
  return executeApiRequest(() => httpService.post(ApiUrls.logisticsWarehouseGroups(), group));
}

export async function deleteWarehouseGroup(warehouseGroupId: number): Promise<IApiExecuteRespone> {
  return executeApiRequest(() => httpService.delete(ApiUrls.logisticsWarehouseGroups(warehouseGroupId)), false);
}
